import {useCallback, useEffect, useRef, useState} from "react";
import {clientFor, type UnsubscribeScope} from "./unsubscribeScope";
import {
	completedState,
	resolvedState,
	submittingState,
	type UnsubscribeState,
} from "./unsubscribeState";

interface UseUnsubscribe {
	state: UnsubscribeState;
	/** Non-null only while a submit is retryable, so the view never invents its own error copy. */
	failed: boolean;
	confirm: () => void;
}

/**
 * Drives the unsubscribe page: resolves the signed token on mount, then records the opt-out on confirm.
 * The transitions themselves live in {@link ./unsubscribeState}; this hook only wires them to the api
 * and to React, so the rules stay testable without rendering anything.
 *
 * @param token the opaque token from the email link's query string
 */
export function useUnsubscribe(token: string | null, scope: UnsubscribeScope = "transfers"): UseUnsubscribe {
	const client = clientFor(scope);
	// ALWAYS starts at "loading", never at a token-derived state: this page is pre-rendered by SSG
	// with no query string, so deriving the initial state from the token would make the first client
	// render disagree with the served HTML and break hydration.
	const [state, setState] = useState<UnsubscribeState>({kind: "loading"});
	const [failed, setFailed] = useState(false);

	useEffect(() => {
		if (!token) {
			setState({kind: "invalid"});
			return;
		}
		let active = true;
		client.status(token)
			.then((status) => active && setState(resolvedState(status)))
			// Any failure here is a token the server would not vouch for; there is nothing the
			// recipient can retry, so the terminal invalid state is the honest outcome.
			.catch(() => active && setState({kind: "invalid"}));
		return () => {
			active = false;
		};
	}, [token, client]);

	// A ref, not the rendered state: the guard has to be readable and settable synchronously so two
	// clicks in one frame cannot both pass it.
	const submitting = useRef(false);

	const confirm = useCallback(() => {
		if (!token || submitting.current) {
			return;
		}
		// The request used to be fired from INSIDE a setState updater. React re-invokes updaters in
		// StrictMode to surface impurity, so a single click sent two POSTs — and the marketing scope
		// made that a second, account-mutating endpoint. Updaters stay pure; the effect lives here.
		submitting.current = true;
		setFailed(false);
		setState(submittingState);
		client.optOut(token)
			.then((status) => setState(completedState(status)))
			.catch(() => {
				setFailed(true);
				setState((current) => (current.kind === "submitting" ? {kind: "confirming", email: current.email} : current));
			})
			.finally(() => {
				submitting.current = false;
			});
	}, [token, client]);

	return {state, failed, confirm};
}
